/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { get, set } from 'idb-keyval';
import type { QueuedSale, QueuedSaleStatus } from './offline-sale.types';

export const OFFLINE_QUEUE_KEY = 'corex-offline-sales';

export async function getQueue(): Promise<QueuedSale[]> {
  return (await get<QueuedSale[]>(OFFLINE_QUEUE_KEY)) ?? [];
}

export async function enqueue(sale: QueuedSale): Promise<void> {
  const queue = await getQueue();
  queue.push(sale);
  await set(OFFLINE_QUEUE_KEY, queue);
}

export async function queueCount(): Promise<number> {
  return (await getQueue()).length;
}

/** Next per-device sequence number (drives the provisional ref). */
export async function nextLocalSeq(deviceId: string): Promise<number> {
  const queue = await getQueue();
  return queue.filter((s) => s.deviceId === deviceId).length + 1;
}

export async function setQueue(sales: QueuedSale[]): Promise<void> {
  await set(OFFLINE_QUEUE_KEY, sales);
}

export async function updateSale(saleClientId: string, patch: Partial<QueuedSale>): Promise<void> {
  const queue = await getQueue();
  await setQueue(queue.map((s) => (s.saleClientId === saleClientId ? { ...s, ...patch } : s)));
}

export async function removeSale(saleClientId: string): Promise<void> {
  const queue = await getQueue();
  await setQueue(queue.filter((s) => s.saleClientId !== saleClientId));
}

export async function getByStatus(status: QueuedSaleStatus): Promise<QueuedSale[]> {
  return (await getQueue()).filter((s) => s.status === status);
}
